import { motion } from "framer-motion";
import { BarChart3, FileText, Download, TrendingUp, Calendar, Tag, ExternalLink, Database } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import FeaturedSpotlight from "@/components/insights/FeaturedSpotlight";
import AlabamaMap from "@/components/AlabamaMap";
import LegislationTracker from "@/components/LegislationTracker";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";

const fallbackArticles = [
  {
    slug: null as string | null,
    tag: "Policy Analysis",
    title: "How Alabama's HB-247 Could Reshape Rural Healthcare Access",
    excerpt: "A deep dive into the implications of the latest healthcare bill and what it means for underserved counties across the state.",
    date: "Mar 25, 2026",
    readTime: "8 min read",
    image_url: null as string | null,
  },
  {
    slug: null as string | null,
    tag: "Funding Trends",
    title: "Federal Grant Landscape: What Changed in Q1 2026",
    excerpt: "An overview of shifting priorities in federal funding and new opportunities for community-based organizations.",
    date: "Mar 18, 2026",
    readTime: "6 min read",
    image_url: null as string | null,
  },
  {
    slug: null as string | null,
    tag: "Impact Story",
    title: "From Data to Action: How Shelby County Secured $2.3M in Education Grants",
    excerpt: "A case study on how one county leveraged The Emerging Market's tools to align their programs with available funding.",
    date: "Mar 10, 2026",
    readTime: "5 min read",
    image_url: null as string | null,
  },
];

const dataHighlights = [
  { label: "Grants Tracked This Month", value: "142", change: "+23%", icon: TrendingUp },
  { label: "Active Legislation", value: "67", change: "+8", icon: FileText },
  { label: "Avg. Grant Size", value: "$385K", change: "+12%", icon: BarChart3 },
  { label: "Partner Matches", value: "54", change: "+31%", icon: Tag },
];

const resources = [
  {
    title: "Financial Empowerment Report & Action Plan — City of Mobile",
    description: "A comprehensive blueprint from the City of Mobile addressing financial empowerment challenges, community outreach findings, and actionable strategies for expanding economic opportunity across all neighborhoods.",
    format: "PDF",
    size: "1.7 MB",
    url: "https://ypfplfhmimgwjtvlswpw.supabase.co/storage/v1/object/public/reports/financial-empowerment-report-mobile-al.pdf",
    citation: "https://www.cityofmobile.gov",
  },
  {
    title: "2026 Alabama Grants Directory",
    description: "Comprehensive listing of federal, state, and philanthropic funding opportunities relevant to Alabama communities.",
    format: "PDF",
    size: "2.4 MB",
  },
  {
    title: "Nonprofit Funding Alignment Guide",
    description: "Step-by-step framework for matching your organization's programs to available grant opportunities.",
    format: "PDF",
    size: "1.8 MB",
  },
  {
    title: "Legislative Impact Scorecard – Q1 2026",
    description: "Quarterly analysis of bills passed, pending, and their projected community impact across key issue areas.",
    format: "PDF",
    size: "3.1 MB",
  },
];

const Insights = () => {
  const { blocks: copy } = useSiteContent("insights");
  const pageIntro = getBlock(
    copy,
    "page_intro",
    "Stay informed with policy analysis, funding trends, and actionable resources designed to help your organization maximize impact.",
  );
  const { data: dbArticles } = useQuery({
    queryKey: ["published-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("published", true)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const articles = (dbArticles && dbArticles.length > 0)
    ? dbArticles.map((a) => ({
        slug: a.slug,
        tag: a.category,
        title: a.title,
        excerpt: a.excerpt || "",
        date: a.published_at ? format(new Date(a.published_at), "MMM d, yyyy") : "",
        readTime: `${Math.max(1, Math.ceil((a.body?.length || 0) / 1000))} min read`,
        image_url: a.image_url,
      }))
    : fallbackArticles;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(135 30% 45%) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Insights</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5">
              Analysis, Data & <span className="text-gradient-primary">Resources</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {pageIntro}
            </p>
          </motion.div>
        </div>
      </section>


      {/* Featured Spotlight */}
      <FeaturedSpotlight />

      {/* Data Highlights */}
      <section className="py-16 border-y border-border bg-muted/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {dataHighlights.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-card border border-border rounded-xl p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                    {item.change}
                  </span>
                </div>
                <div className="font-display text-2xl font-bold">{item.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{item.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">Latest</span>
              <h2 className="font-display text-3xl font-bold mt-2">Articles & Analysis</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {articles.map((article, i) => {
              const Wrapper = article.slug ? Link : "div";
              const wrapperProps = article.slug ? { to: `/insights/${article.slug}` } : {};
              return (
                <Wrapper key={article.title} {...(wrapperProps as any)}>
                  <motion.article
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 + i * 0.1 }}
                    className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary/30 transition-colors cursor-pointer h-full"
                  >
                    {article.image_url ? (
                      <img src={article.image_url} alt={article.title} className="w-full h-44 object-cover" />
                    ) : (
                      <div className="h-44 bg-gradient-to-br from-primary/10 via-accent/5 to-transparent flex items-center justify-center">
                        <FileText className="h-12 w-12 text-primary/30 group-hover:text-primary/50 transition-colors" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                          {article.tag}
                        </span>
                        <span className="text-xs text-muted-foreground">{article.readTime}</span>
                      </div>
                      <h3 className="font-display text-lg font-semibold mb-2 group-hover:text-primary transition-colors leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center text-xs text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5 mr-1.5" />
                        {article.date}
                      </div>
                    </div>
                  </motion.article>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Alabama Map */}
      <div id="map">
        <AlabamaMap />
      </div>

      {/* Legislation Tracker */}
      <div id="legislation">
        <LegislationTracker />
      </div>

      {/* Reports & Resources */}
      <section className="py-20 bg-muted/20 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="mb-10">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Resources</span>
            <h2 className="font-display text-3xl font-bold mt-2">Reports & Downloads</h2>
          </div>

          <div className="space-y-4 max-w-3xl">
            {resources.map((resource, i) => (
              <motion.a
                key={resource.title}
                href={"url" in resource ? (resource as any).url : "#"}
                target="_blank"
                rel="noopener noreferrer"
                download
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.1 }}
                className="bg-card border border-border rounded-xl p-6 flex items-start gap-5 hover:border-primary/30 transition-colors group block"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Download className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    {resource.description}
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="bg-muted px-2 py-0.5 rounded">{resource.format}</span>
                    <span>{resource.size}</span>
                    {"citation" in resource && (
                      <a
                        href={(resource as any).citation}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-primary hover:underline"
                      >
                        Source: cityofmobile.gov
                      </a>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-sm font-medium mt-2">
                  <Download className="h-4 w-4" />
                </div>
              </motion.a>
            ))}
          </div>

          {/* Data Tools */}
          <div className="mt-14">
            <h3 className="font-display text-xl font-bold mb-4">Data Tools</h3>
            <div className="space-y-4">
              <motion.a
                href="/data-tools"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card border border-border rounded-xl p-6 flex items-start gap-5 hover:border-primary/30 transition-colors group block"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <BarChart3 className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    Alabama Community Data Explorer
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Explore county-level data on education, workforce, financial well-being, and health across all 67 Alabama counties.
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="bg-muted px-2 py-0.5 rounded">Interactive</span>
                    <span>Live Census Data</span>
                  </div>
                </div>
                <div className="shrink-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-sm font-medium mt-2">
                  Explore <ExternalLink className="h-3.5 w-3.5" />
                </div>
              </motion.a>
              <motion.a
                href="https://mobile-county-insights.lovable.app"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-card border border-border rounded-xl p-6 flex items-start gap-5 hover:border-primary/30 transition-colors group block"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Database className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-lg group-hover:text-primary transition-colors">
                    Mobile County Insights
                  </h4>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                    Explore property values, land transactions, demographic trends, and housing data for Mobile County, Alabama.
                  </p>
                  <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                    <span className="bg-muted px-2 py-0.5 rounded">Interactive</span>
                    <span>County-level data</span>
                  </div>
                </div>
                <div className="shrink-0 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 text-sm font-medium mt-2">
                  Open <ExternalLink className="h-3.5 w-3.5" />
                </div>
              </motion.a>
            </div>
          </div>
        </div>
      </section>


      <SiteFooter />
    </div>
  );
};

export default Insights;
